import React, { useState } from "react";
import {
  Modal,
  Stack,
  Group,
  TextInput,
  Select,
  NumberInput,
  Button,
  ActionIcon,
  Divider,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";

const SCALE_TYPE_OPTIONS = [
  { value: "COMMISSION", label: "Commission" },
  { value: "MD", label: "MD" },
  { value: "OVERRIDE", label: "Override" },
];

const emptyLine = () => ({ revenue_amount: "", percentage: "" });

// Passing an existing `scale` switches the modal to edit mode, which updates
// the scale in place (PUT) and exposes a delete action.
function AddScaleModal({
  opened,
  onClose,
  onCreated,
  sellingYearId,
  scale = null,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();

  const editingId = scale?.id ?? null;
  const isEditing = editingId != null;

  const [name, setName] = useState("");
  const [scaleType, setScaleType] = useState("COMMISSION");
  const [scaleLines, setScaleLines] = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);

  // Seed the form when the modal opens: from the scale being edited, or empty
  // for a new scale.
  React.useEffect(() => {
    if (!opened) return;
    if (scale) {
      setName(scale.name ?? "");
      setScaleType(scale.scale_type ?? "COMMISSION");
      setScaleLines(
        Array.isArray(scale.scale_lines) && scale.scale_lines.length > 0
          ? scale.scale_lines.map(line => ({
              revenue_amount: line.revenue_amount ?? "",
              percentage: line.percentage ?? "",
            }))
          : [emptyLine()]
      );
    } else {
      setName("");
      setScaleType("COMMISSION");
      setScaleLines([emptyLine()]);
    }
  }, [opened, scale]);

  const updateLine = (index, key, value) =>
    setScaleLines(prev =>
      prev.map((l, i) => (i === index ? { ...l, [key]: value } : l))
    );

  const addLine = () => setScaleLines(prev => [...prev, emptyLine()]);

  const removeLine = index =>
    setScaleLines(prev =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );

  const handleSubmit = async () => {
    if (!name.trim()) {
      showError("Please enter a name");
      return;
    }
    for (const line of scaleLines) {
      if (
        line.revenue_amount === "" ||
        line.revenue_amount == null ||
        line.percentage === "" ||
        line.percentage == null
      ) {
        showError("Please fill out every scale line");
        return;
      }
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const lines = scaleLines.map(line => ({
        revenue_amount: Number(line.revenue_amount),
        percentage: Number(line.percentage),
      }));
      const requestBody = {
        name: name.trim(),
        scale_type: scaleType,
        scale_lines: lines,
      };
      // selling_year_id only applies when creating a new scale.
      if (!isEditing) {
        requestBody.selling_year_id =
          sellingYearId != null ? Number(sellingYearId) : null;
      } else {
        // On edit, pass the scale id so it updates in place.
        requestBody.id = editingId;
      }

      const response = await fetch(
        getApiEndpoint(
          isEditing
            ? `/v1/ps/scale-default/${editingId}`
            : "/v1/ps/scale-default"
        ),
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        showSuccess(
          isEditing
            ? "Scale updated successfully!"
            : "Scale created successfully!"
        );
        if (onCreated) onCreated();
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message ||
            `Failed to ${isEditing ? "update" : "create"} scale`
        );
      }
    } catch (err) {
      handleGenericError(
        err,
        `Failed to ${isEditing ? "update" : "create"} scale`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/ps/scale-default/${editingId}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        showSuccess("Scale deleted successfully!");
        if (onCreated) onCreated();
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to delete scale"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to delete scale");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? "Edit Scale" : "Add Scale"}
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="e.g., Standard Rookies"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          data-autofocus
        />

        <Select
          label="Scale Type"
          data={SCALE_TYPE_OPTIONS}
          value={scaleType}
          onChange={setScaleType}
          allowDeselect={false}
        />

        <Divider label="Scale Lines" labelPosition="left" />

        <Stack gap="xs">
          {scaleLines.map((line, index) => (
            <Group key={index} align="flex-end" gap="xs" wrap="nowrap">
              <NumberInput
                label={index === 0 ? "Retained Revenue" : undefined}
                placeholder="Revenue amount"
                value={line.revenue_amount}
                onChange={value => updateLine(index, "revenue_amount", value)}
                min={0}
                prefix="$"
                thousandSeparator=","
                style={{ flex: 1 }}
              />
              <NumberInput
                label={index === 0 ? "Percentage" : undefined}
                placeholder="Percentage"
                value={line.percentage}
                onChange={value => updateLine(index, "percentage", value)}
                min={0}
                suffix="%"
                style={{ flex: 1 }}
              />
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => removeLine(index)}
                disabled={scaleLines.length === 1}
                aria-label="Remove scale line"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Group justify="flex-start">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={addLine}
            >
              Add Scale Line
            </Button>
          </Group>
        </Stack>

        <Group justify={isEditing ? "space-between" : "flex-end"} mt="md">
          {isEditing && (
            <Button
              color="red"
              variant="outline"
              leftSection={<IconTrash size={16} />}
              onClick={handleDelete}
              loading={submitting}
            >
              Delete
            </Button>
          )}
          <Group>
            <Button variant="subtle" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              {isEditing ? "Update" : "Create"}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddScaleModal;
